import { Inject, Injectable } from '@angular/core';

import { Observable, tap } from 'rxjs';

import { ResourceService } from '../../core/data-access/resource.service';
import { API_CONFIG, ApiConfig } from '../../core/models/api-config.model';
import { RequestToPurchase } from '../../rtp/model/request-to-purchase';
import { Inventory } from '../model/inventory';

/**
 * Service to interact with PDMS Inventory
 */
@Injectable({ providedIn: 'root' })
export class InventoryService extends ResourceService<Inventory, number> {
  constructor(@Inject(API_CONFIG) config: ApiConfig) {
    super('inventory', config.apiUrl);
  }

  /**
   * Saves the inventory to PDMS
   * @param inventory the inventory to save
   * @returns
   */
  save(inventory: Array<Inventory>): Observable<void> {
    return this.http.post<void>(this.endpoint + '/saveAll', inventory);
  }

  /**
   * Find inventory by its fielding id
   * @param id the fielding id
   * @returns inventory for the fielding
   */
  getInventoryByFieldingId(id: number): Observable<Array<Inventory>> {
    return this.http
      .get<Array<Inventory>>(this.endpoint + '/find/' + id)
      .pipe(tap(inventory => this.entityStore.set(inventory)));
  }

  getBaselinesInFielding(selectedFieldingID: number) {
    return this.http.get<string[]>(this.endpoint + '/getBaselines/' + selectedFieldingID);
  }

  getInventoryByFieldingIdEmptyRTP(id: number): Observable<Array<Inventory>> {
    return this.http
      .get<Array<Inventory>>(this.endpoint + '/findNoRTP/' + id)
      .pipe(tap(inventory => this.entityStore.set(inventory)));
  }

  getInventoryByFieldingIdWithRTP(id: number, rtp: number): Observable<Array<Inventory>> {
    return this.http
      .get<Array<Inventory>>(this.endpoint + '/findRTP/' + id + '/' + rtp)
      .pipe(tap(inventory => this.entityStore.set(inventory)));
  }

  findByRTPId(id: number): Observable<Array<Inventory>> {
    return this.http
      .get<Array<Inventory>>(this.endpoint + '/RTP/' + id)
      .pipe(tap(inventory => this.entityStore.set(inventory)));
  }

  findByRTPrqid(id: string): Observable<Array<Inventory>> {
    return this.http
      .get<Array<Inventory>>(this.endpoint + '/rqid/' + id)
      .pipe(tap(inventory => this.entityStore.set(inventory)));
  }

  createRTPInventory(id: number): Observable<Array<Inventory>> {
    return this.http
      .get<Array<Inventory>>(this.endpoint + '/RTP/' + id)
      .pipe(tap(inventory => this.entityStore.set(inventory)));
  }

  // Create sub item using name only
  createRTPInventoryByID(id: number, name: RequestToPurchase) {
    return this.http.post<Inventory>(this.endpoint + '/' + id + '/createRTP/', name);
  }

  // Create sub item using name only
  createSubItem4(id: number, name: Inventory[]) {
    return this.http.post<Inventory>(this.endpoint + '/' + id + '/assign/', name);
  }

  clearRTP(name: Inventory[]) {
    return this.http.post<Inventory>(this.endpoint + '/clear/', name);
  }

  getInventory(id: number, file: File): Observable<Array<Inventory>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<Array<Inventory>>(this.endpoint + '/' + id + '/uploadInventoryFile', formData);
  }

  uploadBOM(id: number, file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<string>(this.endpoint + '/' + id + '/uploadInventoryFile', formData);
  }

  getInventoryByFileName(name: string, fieldingID: number): Observable<Inventory[]> {
    const formData = new FormData();
    formData.append('file', name);
    return this.http.post<Inventory[]>(this.endpoint + '/' + fieldingID + '/getByFileName', formData);
  }

  deleteInventoryFile(filePath: string) {
    return this.http.post<Inventory>(this.endpoint + '/deleteInventoryFile', filePath);
  }

  getListOfFiles(fieldingID: number): Observable<string[]> {
    return this.http.get<string[]>(this.endpoint + '/getFileList/' + fieldingID);
  }

  addInventoryByFileName(fieldingID: number, fileName: string) {
    const formData = new FormData();
    formData.append('file', fileName);
    return this.http.post<Inventory[]>(this.endpoint + '/' + fieldingID + '/addByFileName', formData);
  }

  deleteByFileName(fieldingID: number, fileName: string) {
    const formData = new FormData();
    formData.append('file', fileName);
    return this.http.post<Inventory[]>(this.endpoint + '/' + fieldingID + '/deleteByFileName', formData);
  }

  deleteByBaselineName(fieldingID: number, baselineName: string) {
    const formData = new FormData();
    formData.append('baselineName', baselineName);
    return this.http.post<Inventory[]>(this.endpoint + '/' + fieldingID + '/deleteByBaselineName', formData);
  }

  getInventoryByBaselineName(id: string, fieldingID: number) {
    const formData = new FormData();
    formData.append('id', id);
    return this.http.post<Inventory[]>(this.endpoint + '/' + fieldingID + '/getByBaselineName', formData);
  }

  addInventoryByBaselineID(id: string, fieldingID: number) {
    const formData = new FormData();
    formData.append('id', id);
    return this.http.post<Inventory[]>(this.endpoint + '/' + fieldingID + '/addByBaselineID', formData);
  }
}
